import type { UserMentionDecoratorProps } from '@atlas/lexical-core';
import { UserCardTrigger } from '@core/component/UserCardTrigger';
import { atlasIdToEmail, getDisplayNameParts, tryAtlasId } from '@core/user';
import { firstPartyBotName } from '@queries/bots/first-party-bot-name';
import { cn } from '@ui';
import { createEffect, createMemo, useContext } from 'solid-js';
import { LexicalWrapperContext } from '../../context/LexicalWrapperContext';
import { UPDATE_USER_DISPLAY_NAME_COMMAND } from '../../plugins';

function emailLocalPart(email: string): string {
  return email.split('@')[0] || email;
}

export function UserMention(props: UserMentionDecoratorProps) {
  const lexicalWrapper = useContext(LexicalWrapperContext);
  const selection = () => lexicalWrapper?.selection;

  const isSelectedAsNode = () => {
    const sel = selection();
    if (!sel) return false;
    return sel.type === 'node' && sel.nodeKeys.has(props.key);
  };

  // Convert String wrapper to primitive string
  const userId = () => String(props.userId);
  const propEmail = () => String(props.email);
  const propDisplayName = () => props.displayName?.trim() ?? '';

  const macroId = createMemo(() =>
    props.userId ? tryAtlasId(userId()) : undefined
  );

  const email = createMemo(() => {
    const id = macroId();
    if (id) return atlasIdToEmail(id);
    return propEmail();
  });

  const displayNameParts = createMemo(() => {
    const id = macroId();
    if (!id) return undefined;
    return getDisplayNameParts(id, { emailFallback: 'local-part' });
  });

  const mentionDisplayName = createMemo(() => {
    const botName = firstPartyBotName(userId());
    if (botName) return botName;
    const parts = displayNameParts();
    return (
      parts?.firstName ||
      propDisplayName() ||
      parts?.fullName ||
      emailLocalPart(propEmail())
    );
  });

  const tooltipDisplayName = createMemo(
    () => displayNameParts()?.fullName || mentionDisplayName()
  );

  createEffect(() => {
    // Historical bot mentions render their current name without rewriting saved content.
    if (firstPartyBotName(userId())) return;
    const nextDisplayName = mentionDisplayName().trim();
    if (!nextDisplayName || nextDisplayName === propDisplayName()) return;

    setTimeout(() => {
      lexicalWrapper?.editor?.dispatchCommand(
        UPDATE_USER_DISPLAY_NAME_COMMAND,
        {
          [userId()]: nextDisplayName,
        }
      );
    });
  });

  return (
    <UserCardTrigger
      placement="top"
      triggerAs="span"
      user={{
        displayName: tooltipDisplayName() || email() || propEmail(),
        email: email() || propEmail(),
        id: userId(),
      }}
      trigger={
        <span
          class={cn(
            'relative p-0.5 cursor-default rounded-md bg-accent/8 hover:bg-accent/20 focus:bg-accent/20 text-accent',
            isSelectedAsNode() && 'bg-active'
          )}
        >
          <span
            data-user-id={props.userId}
            data-email={props.email}
            data-display-name={mentionDisplayName()}
            data-user-mention="true"
          >
            @{mentionDisplayName()}
          </span>
        </span>
      }
    />
  );
}
